/**
 * Wishlist Manager
 * Stores wished products in localStorage, mirrors the recently-viewed-products pattern.
 * Dispatches 'wishlistUpdated' custom event on every change.
 */

class WishlistManager {
  constructor() {
    this.storageKey = 'wishlist-products';
  }

  getAll() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('WishlistManager: Could not read localStorage', e);
      return [];
    }
  }

  has(productId) {
    return this.getAll().some(p => p.id === Number(productId));
  }

  add(productData) {
    const items = this.getAll().filter(p => p.id !== Number(productData.id));
    items.unshift({ ...productData, id: Number(productData.id) });
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(items));
    } catch (e) {
      console.error('WishlistManager: Could not write to localStorage', e);
    }
    this._dispatch();
  }

  remove(productId) {
    const items = this.getAll().filter(p => p.id !== Number(productId));
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(items));
    } catch (e) {
      console.error('WishlistManager: Could not write to localStorage', e);
    }
    this._dispatch();
  }

  toggle(btn) {
    // Redirect to login if the customer is not authenticated
    if (window.wishlistConfig && !window.wishlistConfig.customerLoggedIn) {
      window.location.href = window.wishlistConfig.loginUrl || '/account/login?return_url=/pages/wishlist';
      return;
    }

    const id = btn.dataset.productId;
    if (this.has(id)) {
      this.remove(id);
    } else {
      this.add({
        id: id,
        handle: btn.dataset.productHandle || '',
        title: btn.dataset.productTitle || '',
        url: btn.dataset.productUrl || '',
        image: btn.dataset.productImage || '',
        price: btn.dataset.productPrice || '',
      });
    }
  }

  _dispatch() {
    document.dispatchEvent(
      new CustomEvent('wishlistUpdated', { detail: { items: this.getAll() } })
    );
  }
}

// Expose globally so the wishlist page script can access it
window.WishlistManager = new WishlistManager();

// ─── Button init ─────────────────────────────────────────────────────────────

function initWishlistButtons() {
  // Bind click on any newly added, unbound buttons
  document.querySelectorAll('.wishlist-btn:not([data-wishlist-bound])').forEach(btn => {
    btn.dataset.wishlistBound = 'true';
    btn.addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
      window.WishlistManager.toggle(btn);
    });
  });

  // Sync active state on all buttons (including already-bound ones)
  document.querySelectorAll('.wishlist-btn').forEach(btn => {
    btn.classList.toggle('is-wishlisted', window.WishlistManager.has(btn.dataset.productId));
    btn.setAttribute(
      'aria-label',
      window.WishlistManager.has(btn.dataset.productId)
        ? 'Remove from wishlist'
        : 'Add to wishlist'
    );
  });
}

// ─── Header counter ───────────────────────────────────────────────────────────

function updateWishlistCounter() {
  const counter = document.querySelector('.wt-wishlist__counter');
  if (!counter) return;
  const count = window.WishlistManager.getAll().length;
  counter.textContent = count;
  counter.style.display = count > 0 ? '' : 'none';
}

// ─── Boot ─────────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  initWishlistButtons();
  updateWishlistCounter();

  // Watch for dynamically injected buttons (quick-add drawer, infinite scroll, etc.)
  const observer = new MutationObserver(initWishlistButtons);
  observer.observe(document.body, { childList: true, subtree: true });
});

document.addEventListener('wishlistUpdated', () => {
  initWishlistButtons();
  updateWishlistCounter();
});
